import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Popover,
  TextField,
  List,
  ListItemButton,
  Divider,
  Button,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";

const PRESETS = [
  {
    key: "yesterday",
    label: "Yesterday",
    getRange: (today) => {
      const day = today.subtract(1, "day");
      return { start: day, end: day };
    },
  },
  {
    key: "last7d",
    label: "Last 7 Days",
    getRange: (today) => ({ start: today.subtract(6, "day"), end: today }),
  },
  {
    key: "last30d",
    label: "Last 30 Days",
    getRange: (today) => ({ start: today.subtract(29, "day"), end: today }),
  },
  {
    key: "today",
    label: "Today",
    getRange: (today) => ({ start: today, end: today }),
  },
  {
    key: "tomorrow",
    label: "Tomorrow",
    getRange: (today) => {
      const day = today.add(1, "day");
      return { start: day, end: day };
    },
  },
  {
    key: "next7d",
    label: "Next 7 Days",
    getRange: (today) => ({ start: today, end: today.add(6, "day") }),
  },
  {
    key: "next30d",
    label: "Next 30 Days",
    getRange: (today) => ({ start: today, end: today.add(29, "day") }),
  },
];

const DEFAULT_PRESET_KEY = "today";

const toApiDate = (d) => (d && d.isValid() ? d.format("YYYY-MM-DD") : "");

const formatTriggerRange = (start, end) => {
  if (!start && !end) return "Select dates";
  if (start && end) return `${start.format("MMM D")} – ${end.format("MMM D")}`;
  if (start) return start.format("MMM D, YYYY");
  return end.format("MMM D, YYYY");
};

const formatInputDate = (d) => (d && d.isValid() ? d.format("MMM D, YYYY") : "");

const presetLabel = (key) => {
  if (key === "custom") return "Custom range";
  if (!key) return "Date Range";
  return PRESETS.find((p) => p.key === key)?.label || "Date Range";
};

function RangeDay(props) {
  const { day, start, end, ...other } = props;
  const isStart = start && day.isSame(start, "day");
  const isEnd = end && day.isSame(end, "day");
  const inRange =
    start &&
    end &&
    day.isAfter(start, "day") &&
    day.isBefore(end, "day");

  return (
    <PickersDay
      {...other}
      day={day}
      sx={{
        ...(isStart || isEnd
          ? {
              bgcolor: "#2563eb !important",
              color: "#fff !important",
              "&:hover": { bgcolor: "#1d4ed8 !important" },
            }
          : {}),
        ...(inRange
          ? {
              bgcolor: "#eff6ff",
              borderRadius: 0,
              "&:hover": { bgcolor: "#dbeafe" },
            }
          : {}),
      }}
    />
  );
}

/**
 * Date range picker with presets — matches task board filter design.
 */
const DateRangeFilter = ({ value = { start: "", end: "" }, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [committedPreset, setCommittedPreset] = useState(() => {
    if (!value?.start && !value?.end) return "";
    return DEFAULT_PRESET_KEY;
  });
  const [draftPreset, setDraftPreset] = useState(() => {
    if (!value?.start && !value?.end) return "";
    return DEFAULT_PRESET_KEY;
  });
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [focusedField, setFocusedField] = useState("start");
  const [calendarMonth, setCalendarMonth] = useState(dayjs());

  useEffect(() => {
    if (!value?.start && !value?.end) {
      setCommittedPreset("");
      setDraftPreset("");
      setDraftStart(null);
      setDraftEnd(null);
    }
  }, [value?.start, value?.end]);

  const committedStart = value.start ? dayjs(value.start) : null;
  const committedEnd = value.end ? dayjs(value.end) : null;

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setDraftStart(committedStart);
    setDraftEnd(committedEnd);
    setDraftPreset(committedPreset);
    setFocusedField("start");
    setCalendarMonth(committedStart || committedEnd || dayjs());
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const applyPreset = (preset) => {
    const today = dayjs().startOf("day");
    const { start, end } = preset.getRange(today);
    onChange({
      start: toApiDate(start),
      end: toApiDate(end),
    });
    setCommittedPreset(preset.key);
    handleClose();
  };

  const handleDaySelect = (newDay) => {
    if (!newDay) return;

    if (focusedField === "start") {
      const nextEnd = draftEnd && newDay.isAfter(draftEnd, "day") ? newDay : draftEnd;
      setDraftStart(newDay);
      setDraftEnd(nextEnd || newDay);
      setDraftPreset("custom");
      setFocusedField("end");
    } else {
      const nextStart = draftStart && newDay.isBefore(draftStart, "day") ? newDay : draftStart;
      setDraftStart(nextStart || newDay);
      setDraftEnd(newDay);
      setDraftPreset("custom");
    }
    setCalendarMonth(newDay);
  };

  const handleApply = () => {
    onChange({
      start: toApiDate(draftStart),
      end: toApiDate(draftEnd),
    });
    setCommittedPreset(draftPreset);
    handleClose();
  };

  const handleClear = () => {
    onChange({ start: "", end: "" });
    setCommittedPreset("");
    setDraftPreset("");
    setDraftStart(null);
    setDraftEnd(null);
    handleClose();
  };

  const triggerRange = useMemo(
    () => formatTriggerRange(committedStart, committedEnd),
    [committedStart, committedEnd]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          boxSizing: "border-box",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          minWidth: 168,
          backgroundColor: "#ffffff",
          cursor: "pointer",
          px: 1.25,
          py: 0.75,
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: "#cbd5e1" },
          ...(open && { borderColor: "#2563eb" }),
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
        <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#1e293b",
              whiteSpace: "nowrap",
            }}
          >
            {presetLabel(committedPreset)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#64748b",
              whiteSpace: "nowrap",
            }}
          >
            {triggerRange}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              overflow: "hidden",
              mt: 0.75,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box sx={{ display: "flex" }}>
          <Box sx={{ p: 2, width: 320 }}>
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#2563eb", mb: 0.4 }}
            >
              {presetLabel(draftPreset)}
            </Typography>
            <Typography
              sx={{ fontSize: "0.72rem", color: "#94a3b8", mb: 1.5, lineHeight: 1.35 }}
            >
              Date Filters by due date
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <TextField
                size="small"
                value={formatInputDate(draftStart)}
                placeholder="From"
                onClick={(e) => {
                  e.stopPropagation();
                  setFocusedField("start");
                }}
                InputProps={{ readOnly: true }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    bgcolor: "#fff",
                    ...(focusedField === "start"
                      ? { "& fieldset": { borderColor: "#2563eb", borderWidth: 2 } }
                      : {}),
                  },
                }}
              />
              <Typography sx={{ color: "#94a3b8" }}>–</Typography>
              <TextField
                size="small"
                value={formatInputDate(draftEnd)}
                placeholder="To"
                onClick={(e) => {
                  e.stopPropagation();
                  setFocusedField("end");
                }}
                InputProps={{ readOnly: true }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    bgcolor: "#fff",
                    ...(focusedField === "end"
                      ? { "& fieldset": { borderColor: "#2563eb", borderWidth: 2 } }
                      : {}),
                  },
                }}
              />
            </Box>

            <DateCalendar
              value={focusedField === "start" ? draftStart : draftEnd}
              referenceDate={calendarMonth}
              onMonthChange={setCalendarMonth}
              onChange={handleDaySelect}
              slots={{
                day: RangeDay,
                leftArrowIcon: ChevronLeftIcon,
                rightArrowIcon: ChevronRightIcon,
              }}
              slotProps={{
                day: { start: draftStart, end: draftEnd },
              }}
              sx={{
                width: "100%",
                minHeight: 320,
                "& .MuiPickersCalendarHeader-label": {
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: "#64748b",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  fontSize: "0.75rem",
                  color: "#94a3b8",
                },
                "& .MuiPickersDay-root": {
                  fontSize: "0.85rem",
                },
              }}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box
            sx={{
              width: 168,
              display: "flex",
              flexDirection: "column",
              minHeight: 380,
            }}
          >
            <List disablePadding sx={{ py: 1, flex: 1 }}>
              {PRESETS.map((preset) => {
                const isActive = committedPreset === preset.key;
                return (
                  <ListItemButton
                    key={preset.key}
                    onClick={() => applyPreset(preset)}
                    sx={{
                      py: 1,
                      px: 2,
                      "&:hover": { bgcolor: "rgba(37, 99, 235, 0.04)" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "#2563eb" : "#64748b",
                      }}
                    >
                      {preset.label}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                p: 1.5,
                borderTop: "1px solid #e8eaed",
              }}
            >
              <Button
                onClick={handleClear}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  minWidth: 72,
                }}
              >
                Clear
              </Button>
              <Button
                onClick={handleApply}
                variant="contained"
                size="small"
                sx={{
                  textTransform: "none",
                  bgcolor: "#2563eb",
                  minWidth: 72,
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;
